// src/app/shared/components/profile-sidebar/profile-sidebar.component.ts
import { Component, ChangeDetectionStrategy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../core/services/auth.service';
import { MemberService } from '../../../core/services/member.service';

@Component({
  selector: 'app-profile-sidebar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [CommonModule, RouterModule],
  templateUrl: './profile-sidebar.component.html',
  styleUrls: ['./profile-sidebar.component.scss'],
})
export class ProfileSidebarComponent implements OnInit {
  private static readonly defaultAvatar = '/assets/default-avatar.png';

  userName: string = 'Usuário';
  userAvatar: string = ProfileSidebarComponent.defaultAvatar;
  userSince: string | number = '—';
  
  companyName: string | null = null;
  companyCnpj: string | null = null;
  companyLogo: string | null = null;

  profilePercent: number = 0;
  profileCompleted: boolean = false;

  /** Lista de membros (`/members`) — desativa o item lateral "Membros". */
  membersNavDisabled = false;

  /** Painel de controle (`/control`) — desativa o item lateral homônimo. */
  controlNavDisabled = false;

  /** Totais vindos das APIs following / followers (per_page mínimo + pagination). */
  followingTotal: number | null = null;
  followersTotal: number | null = null;

  private readonly router = inject(Router);

  constructor(
    private authService: AuthService,
    private memberService: MemberService,
  ) {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.syncSidebarNavState());
  }

  ngOnInit(): void {
    this.syncSidebarNavState();
    const memberData = this.authService.getMemberData();
    if (memberData) {
      this.userName = memberData.name || 'Usuário';
      const avatarUrl = this.resolveAvatarUrl(memberData.avatar);
      if (avatarUrl) {
        this.userAvatar = avatarUrl;
      }
      
      if (memberData.companies && memberData.companies.length > 0) {
        const mainCompany = memberData.companies[0];
        this.companyName = mainCompany.trade_name || mainCompany.legal_name || null;
        this.companyCnpj = mainCompany.cnpj || null;
        this.companyLogo = mainCompany.logo || null;
      }

      // New fields
      if (memberData.profile_completion_percent != null) {
        this.profilePercent = memberData.profile_completion_percent;
      }
      this.profileCompleted = !!memberData.completed_profile_flag;

      if (memberData.created_at) {
        const year = new Date(memberData.created_at).getFullYear();
        this.userSince = isNaN(year) ? '—' : year;
      }
    }

    forkJoin([
      this.memberService.listFollowing(1, 1),
      this.memberService.listFollowers(1, 1),
    ]).subscribe({
      next: ([fwing, fwers]) => {
        const fi = fwing.content?.pagination;
        const fe = fwers.content?.pagination;
        if (fi) this.followingTotal = fi.total_items;
        if (fe) this.followersTotal = fe.total_items;
      },
      error: () => {
        /* conta opcional na lateral */
      },
    });
  }

  logout(event: Event) {
    event.preventDefault();
    this.authService.logout();
    this.router.navigateByUrl('/login');
  }

  private syncSidebarNavState(): void {
    const path = this.router.url.split(/[?#]/)[0];
    this.membersNavDisabled = path === '/members';
    this.controlNavDisabled = path === '/control';
  }

  /** Avatar do login (`member_data`): string ou `{ url }`. */
  private resolveAvatarUrl(avatar: unknown): string | null {
    if (avatar == null) return null;
    if (typeof avatar === 'string') {
      const s = avatar.trim();
      return s.length ? s : null;
    }
    if (typeof avatar === 'object' && avatar !== null && 'url' in avatar) {
      const u = (avatar as { url: unknown }).url;
      if (typeof u === 'string' && u.trim()) return u.trim();
    }
    return null;
  }
}
